import Table from "common/Table/Table";
import { ColumnsType } from "antd/es/table";
import PaymentStatusTag from "features/settings/Billing/PaymentStatusTag";
import {
  LanguageNameSpaces,
  PaymentDetails,
  SubscriptionType,
} from "@shared/types";
import { formatDate, sortRawDates } from "@shared/utils/date";
import styled, { css, useTheme } from "styled-components";
import { ReactNode } from "react";
import usePayments from "@shared/features/settings/subscription/hooks/usePayments";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface PaymentsTableProps {
  subscriptionType: SubscriptionType;
  /** Custom empty state (new look). */
  emptyText?: ReactNode;
}

const PaymentsTable = ({ subscriptionType, emptyText }: PaymentsTableProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { isFetching, paymentsData, getPaymentDate } = usePayments({
    subscriptionType,
  });
  const isGroupPayments = subscriptionType === SubscriptionType.GROUP;
  const { isNewLook } = useTheme();

  const columns: ColumnsType<PaymentDetails> = [
    {
      title: t("payments_table.date"),
      dataIndex: "paid_datetime",
      key: "paid_datetime",
      showSorterTooltip: false,
      defaultSortOrder: "descend",
      sorter: (a, b) => {
        const aDatetime = getPaymentDate(a);
        const bDatetime = getPaymentDate(b);
        return sortRawDates(aDatetime, bDatetime);
      },
      render: (_, paymentDetails) => {
        const datetime = getPaymentDate(paymentDetails);
        return formatDate(datetime, true, true);
      },
    },
    {
      title: t("payments_table.price"),
      dataIndex: "cost",
      key: "cost",
      showSorterTooltip: false,
      // New look: amounts line up on the right.
      ...(isNewLook && { align: "right" as const }),
      render: (price) => price && `$${price} USD`,
    },
    {
      title: isGroupPayments
        ? t("payments_table.group_name")
        : t("payments_table.plan"),
      dataIndex: isGroupPayments ? "group_name" : "subscription_type",
      key: "subscription_type",
      showSorterTooltip: false,
      render: (value) => <GroupNameWrapper>{value}</GroupNameWrapper>,
    },
    {
      title: t("payments_table.duration"),
      dataIndex: "duration",
      key: "duration",
      showSorterTooltip: false,
    },
    {
      title: t("payments_table.action"),
      dataIndex: "action",
      key: "action",
      showSorterTooltip: false,
    },
    {
      title: t("payments_table.method"),
      dataIndex: "payment_option",
      key: "payment_option",
      showSorterTooltip: false,
    },
    {
      title: t("payments_table.status"),
      dataIndex: "status",
      key: "status",
      showSorterTooltip: false,
      render: (_, paymentDetails) => (
        <PaymentStatusTag paymentDetails={paymentDetails} />
      ),
    },
  ];

  return (
    <Table<PaymentDetails>
      columns={columns}
      dataSource={paymentsData}
      loading={isFetching}
      rowClassName="table-three-quarters-padding"
      {...(emptyText ? { locale: { emptyText } } : {})}
    />
  );
};

export default PaymentsTable;

const GroupNameWrapper = styled.div`
  max-width: 200px;
  white-space: break-spaces;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `}
`;
